import type { InfiniteData, UseInfiniteQueryResult } from '@tanstack/react-query'
import { useInfiniteScroll } from '@/shared/composables/useInfiniteScroll'
import { ScrollTopButton } from '@/shared/ui/ScrollTopButton'
import { Link } from 'react-router-dom'
import { QueryState } from '@/shared/ui/QueryState'
import { EmptyStateCard } from '@/shared/ui/EmptyState'
import { DefaultLayout } from '@/shared/ui/DefaultLayout'
import { CityIcon } from '@/shared/ui/icons/CityIcon'
import { CategoryTabBar } from '@/shared/ui/CategoryTabBar'
import { FilterModalPc } from '@/shared/ui/FilterModalPc'
import { RegisteredCompaniesRow } from '@/public/features/org/ui/RegisteredCompaniesRow'
import { useListFilter } from '@/shared/composables/useListFilter'
import { InfoIcon } from '@/shared/ui/icons/InfoIcon'
import type { Paged, SolutionCard } from '@/shared/lib/apiTypes'
import { SolutionListCard } from '@/shared/ui/IssueCard'
import { EMPTY_ORG_CARD } from '@/public/features/home/constants/homeEmpty'
import { useCardLabels } from '@/shared/lib/cardLabels'
import { profileTone } from '@/shared/lib/profileTone'
import { SOLUTION_CARD_CATEGORY_MAX } from '@/shared/constants/cards'
import { CARD_SHELL_CLASS } from '@/shared/constants/cards'

export function SolutionListPc({ query }: { query: UseInfiniteQueryResult<InfiniteData<Paged<SolutionCard>>> }) {
  const { dongLabel, categoryLabel, isReady } = useCardLabels()

  const endRef = useInfiniteScroll(
    () => void query.fetchNextPage(),
    query.hasNextPage && !query.isFetchingNextPage,
  )

  const all = query.data?.pages.flatMap((page) => page.items) ?? []
  const f = useListFilter(all, {
    categories: (c) => c.categoryCodes,
    targets: (c) => c.targetCodes,
    regions: (c) => c.serviceDongCodes ?? [],
  })

  return (
    <DefaultLayout tree="pc" className="[--body-w:1000px]">
      <div className="layout-body pt-[27px] pb-[30px]">
        <RegisteredCompaniesRow paddingTop="pt-0" />

        <section className="pt-[42px]">
          <CategoryTabBar
            groups={f.groups.data ?? []}
            selected={f.group}
            onSelect={f.selectGroup}
            hasFilter={f.hasFilter}
            filterOpen={f.sheetOpen}
            onOpenFilter={() => f.setSheetOpen(true)}
          />

          <QueryState
            isPending={query.isPending || !isReady}
            error={query.error}
            isEmpty={f.filtered.length === 0}
            empty={
              <div className="flex justify-center pt-6 pb-2">
                <EmptyStateCard {...EMPTY_ORG_CARD} />
              </div>
            }
          >
            <div className="mt-[22px] grid grid-cols-3 items-stretch gap-4">
              {f.filtered.map((c) => {
                return (
                  <Link
                    key={c.id}
                    className={`flex ${CARD_SHELL_CLASS} text-inherit no-underline`}
                    to={`/solutions/${c.id}`}
                  >
                    <SolutionListCard
                      profileIcon={<CityIcon size={20} />}
                      profileTone={profileTone(c.org.id)}
                      orgName={c.org.name}
                      categories={c.categoryCodes.slice(0, SOLUTION_CARD_CATEGORY_MAX).map(categoryLabel)}
                      title={c.title}
                      summary={c.problemSolved}
                      {...(c.accessCondition !== null && c.accessCondition.length > 0
                        ? { notice: c.accessCondition, noticeIcon: <InfoIcon /> }
                        : {})}
                      metaItems={(c.serviceDongCodes ?? []).map(dongLabel)}
                    />
                  </Link>
                )
              })}
            </div>

      <div ref={endRef} aria-hidden="true" />
      {query.isFetchingNextPage && (
        <p className="m-0 py-4 text-center text-body-sm text-black-100">더 불러오는 중…</p>
      )}
          </QueryState>
        </section>
      </div>

      {f.sheetOpen && (
        <FilterModalPc
          label="기업 해결카드 필터"
          value={f.filter}
          categories={f.categories.data ?? []}
          lifecycle={f.lifecycle.data ?? []}
          situation={f.situation.data ?? []}
          dongs={f.dongs.data ?? []}
          onApply={f.applyFilter}
          onClose={() => f.setSheetOpen(false)}
        />
      )}

      <ScrollTopButton />
    </DefaultLayout>
  )
}
